import { useState, useCallback } from "react";
import {
  _get_campaigns,
  _get_campaign,
  _create_campaign,
  _update_campaign,
  _start_campaign,
  _pause_campaign,
  _resume_campaign,
  _get_campaign_stats,
  _get_campaign_report,
  _delete_campaign,
  _preview_campaign_template,
  _resend_campaign,
  _get_recipient_counts,
} from "../../../common/axios_services";

const useCampaignData = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [report, setReport] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCampaigns = useCallback(async (params = {}) => {
    try {
      if (!params.silent) setLoading(true);
      const res = await _get_campaigns(params);
      setCampaigns(res?.data?.data || []);
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to load campaigns");
      return { success: false };
    } finally {
      if (!params.silent) setLoading(false);
    }
  }, []);

  const fetchCampaign = useCallback(async (id) => {
    try {
      setLoading(true);
      const res = await _get_campaign(id);
      setCurrentCampaign(res?.data?.data);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to load campaign");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const createCampaign = useCallback(async (data) => {
    try {
      setLoading(true);
      const res = await _create_campaign(data);
      await fetchCampaigns();
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to create campaign");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCampaign = useCallback(async (id, data) => {
    try {
      setLoading(true);
      const res = await _update_campaign(id, data);
      await fetchCampaigns();
      return { success: true };
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update campaign",
      );
      throw err;
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const startCampaign = useCallback(async (id) => {
    try {
      setLoading(true);
      const res = await _start_campaign(id);
      await fetchCampaigns();
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to start campaign");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const pauseCampaign = useCallback(async (id) => {
    try {
      setLoading(true);
      const res = await _pause_campaign(id);
      await fetchCampaigns();
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to pause campaign");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const resumeCampaign = useCallback(async (id) => {
    try {
      setLoading(true);
      const res = await _resume_campaign(id);
      await fetchCampaigns();
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to resume campaign");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async (id) => {
    try {
      setLoading(true);
      const res = await _get_campaign_stats(id);
      setStats(res?.data?.data);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to load stats");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReport = useCallback(async (id) => {
    try {
      setLoading(true);
      const res = await _get_campaign_report(id);
      setReport(res?.data?.data);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to load report");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteCampaign = useCallback(async (id) => {
    try {
      setLoading(true);
      const res = await _delete_campaign(id);
      await fetchCampaigns();
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to delete campaign");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const previewTemplate = useCallback(async (id, sampleData) => {
    try {
      setLoading(true);
      const res = await _preview_campaign_template(id, sampleData);
      setTemplatePreview(res?.data?.data);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to preview template");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const resendCampaign = useCallback(async (id) => {
    try {
      setLoading(true);
      const res = await _resend_campaign(id);
      await fetchCampaigns();
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to resend campaign");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecipientCounts = useCallback(async (params = {}) => {
    try {
      const res = await _get_recipient_counts(params);
      const data = res?.data?.data || res?.data;
      if (data && typeof data === "object") {
        return { success: true, data };
      }
      throw new Error("Invalid response format");
    } catch (err) {
      console.error("Failed to load recipient counts", err);
      return {
        success: false,
        data: {
          byRecipientType: { ALL: 0, BUYER: 0, DRIVER: 0, BUSINESS: 0, RESTAURANT: 0, AMBASSADOR: 0 },
          bySmsVerification: { ALL: 0, VERIFIED: 0, UNVERIFIED: 0 },
          byOnboardingStage: { ALL: 0, PRE_ONBOARD: 0, POST_ONBOARD: 0 },
        },
      };
    }
  }, []);

  return {
    campaigns,
    currentCampaign,
    stats,
    report,
    templatePreview,
    loading,
    error,
    fetchCampaigns,
    fetchCampaign,
    createCampaign,
    updateCampaign,
    startCampaign,
    pauseCampaign,
    resumeCampaign,
    fetchStats,
    fetchReport,
    deleteCampaign,
    previewTemplate,
    resendCampaign,
    fetchRecipientCounts,
  };
};

export default useCampaignData;
